import { useCallback, useEffect, useRef } from 'react';
import { Editor } from 'primereact/editor';

// PrimeReact's Editor re-applies its controlled `value` right after Quill's onLoad;
// with quill@2 that internal effect can wipe server-provided content back to an empty
// paragraph. Re-apply the value on the next frame, once Quill and PrimeReact have settled.
const normalizeEditorHtml = (html: string): string => html
    .replace(/<p>\s*((?:<li[\s\S]*?<\/li>\s*)+)<\/p>/gi, '<ul>$1</ul>')
    .replace(/<p>\s*<\/p>/gi, '<p><br></p>');

export function useEditorServerSync(value: string) {
    const editorRef = useRef<Editor>(null);
    const appliedRef = useRef(false);

    const syncFromValue = useCallback((val: string, quillInstance?: unknown) => {
        if (!val || appliedRef.current) return;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const quill = (quillInstance as any) ?? (editorRef.current as any)?.getQuill?.();
        if (!quill) return;

        const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(val);
        const html = looksLikeHtml ? normalizeEditorHtml(val) : val.replace(/\n/g, '<br/>');

        appliedRef.current = true;

        window.requestAnimationFrame(() => {
            // Go through the clipboard converter rather than writing root.innerHTML: Quill's
            // Break blot deletes any <br> that has siblings, so "<p>a<br>b</p>" would collapse
            // into a single line. 'silent' keeps the original server HTML as the bound value.
            if (quill.clipboard?.convert && quill.setContents) {
                quill.setContents(quill.clipboard.convert({ html, text: '' }), 'silent');
            } else if (quill.clipboard?.dangerouslyPasteHTML) {
                quill.clipboard.dangerouslyPasteHTML(0, html, 'api');
            } else if (!looksLikeHtml) {
                quill.setText(val, 'api');
            }
        });
    }, []);

    useEffect(() => {
        syncFromValue(value);
    }, [value, syncFromValue]);

    const onLoad = useCallback((quill: unknown) => {
        syncFromValue(value, quill);
    }, [value, syncFromValue]);

    // Call before loading a new external value (e.g. switching template/record)
    // so the next non-empty value is applied again.
    const reset = useCallback(() => {
        appliedRef.current = false;
    }, []);

    // Call once the user starts typing, so a later render carrying the same
    // (now stale) server value doesn't stomp on what they're typing.
    const markApplied = useCallback(() => {
        appliedRef.current = true;
    }, []);

    return { editorRef, onLoad, reset, markApplied };
}
